import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ConfigService } from './config.service';

/**
 * TT-128: the software switcher shown in the top right of every TradeTap
 * product. The API says which software this person can use; the addresses come
 * from this environment's config, so staging never links into production.
 */
export interface TradeTapAppLink {
  key: string;
  name: string;
  hasAccess: boolean;
  comingSoon: boolean;
  url: string;
}

interface UserAppResponse {
  key: string;
  name: string;
  hasAccess: boolean;
  comingSoon: boolean;
}

/**
 * Shown if the API cannot be reached, so the menu degrades to what it did
 * before rather than disappearing. Everything the user cannot be sure of is
 * greyed rather than offered.
 */
const FALLBACK: UserAppResponse[] = [
  { key: 'VTI', name: 'VTI', hasAccess: true, comingSoon: false },
  { key: 'TRAINING_HUB', name: 'Training Hub', hasAccess: true, comingSoon: false },
  { key: 'TRADETAP_PRO', name: 'TradeTap Pro', hasAccess: false, comingSoon: false },
  { key: 'JUMPSTART', name: 'Jumpstart', hasAccess: false, comingSoon: false },
  { key: 'TRADETAP_HOME', name: 'TradeTap Home', hasAccess: false, comingSoon: true },
];

@Injectable({ providedIn: 'root' })
export class TradeTapAppsService {
  private cached?: Observable<TradeTapAppLink[]>;

  constructor(
    private readonly http: HttpClient,
    private readonly config: ConfigService,
  ) {}

  /** Cached for the session: access does not change while someone is signed in. */
  apps(): Observable<TradeTapAppLink[]> {
    if (!this.cached) {
      this.cached = this.http
        .get<UserAppResponse[]>(`${this.config.host}/users/me/apps`)
        .pipe(
          catchError(() => of(FALLBACK)),
          map((apps) => apps.map((a) => this.withUrl(a))),
          shareReplay(1),
        );
    }
    return this.cached;
  }

  /**
   * An app with no address configured for this environment is not reachable
   * from here, whatever the API says, so it is shown greyed rather than as a
   * link that goes nowhere.
   */
  private withUrl(app: UserAppResponse): TradeTapAppLink {
    const urls = (environment as { appUrls?: Record<string, string> }).appUrls ?? {};
    const url = urls[app.key] ?? '';
    return { ...app, url, hasAccess: app.hasAccess && !!url && !app.comingSoon };
  }
}
